import { createContext, useContext, useMemo, useEffect, type ReactNode } from 'react';
import { initBoardSchedule, recalculateSchedule } from './roadmapStore';
import { initBoardColumnsStore, type ColumnsStore } from './columnsStore';
import { initBoardRequirementsStore, type BoardRequirementsStore } from './boardRequirementsStore';
import { initBoardTaskViewStore, type BoardTaskViewStore } from './boardTaskViewStore';
import { getBoardById } from '../../stores/boardsStore';
import { BOARD_START_DATE } from '../../lib/dateUtils';
import { uiStore } from '../../stores/uiStore';

interface ActiveBoardContextValue {
  boardId: string;
  boardStartDate: string;
  boardName: string;
  columnsStore: ColumnsStore;
  boardRequirementsStore: BoardRequirementsStore;
  boardTaskViewStore: BoardTaskViewStore;
}

const ActiveBoardCtx = createContext<ActiveBoardContextValue | null>(null);

interface ActiveBoardProviderProps {
  boardId: string;
  children: ReactNode;
}

export function ActiveBoardProvider({ boardId, children }: ActiveBoardProviderProps) {
  const board = getBoardById(boardId);
  const boardStartDate = board?.startDate ?? BOARD_START_DATE;
  const boardName = board?.name ?? 'Board';

  // Initialize board-specific stores
  const stores = useMemo(() => {
    const columnsStore = initBoardColumnsStore(boardId);
    const boardRequirementsStore = initBoardRequirementsStore(boardId);
    const boardTaskViewStore = initBoardTaskViewStore(boardId);

    // Initialize schedule cache for this board
    initBoardSchedule(boardId, boardStartDate);

    return { columnsStore, boardRequirementsStore, boardTaskViewStore };
  }, [boardId, boardStartDate]);

  // Reset UI state when board changes
  useEffect(() => {
    uiStore.send({ type: 'ui.reset' });
    // Recalculate schedule for the new board
    recalculateSchedule(boardId);
  }, [boardId]);

  const value = useMemo<ActiveBoardContextValue>(() => ({
    boardId,
    boardStartDate,
    boardName,
    columnsStore: stores.columnsStore,
    boardRequirementsStore: stores.boardRequirementsStore,
    boardTaskViewStore: stores.boardTaskViewStore,
  }), [boardId, boardStartDate, boardName, stores]);

  return (
    <ActiveBoardCtx.Provider value={value}>
      {children}
    </ActiveBoardCtx.Provider>
  );
}

// ============================================
// Hooks to access active board context
// ============================================

export function useActiveBoardContext(): ActiveBoardContextValue {
  const ctx = useContext(ActiveBoardCtx);
  if (!ctx) {
    throw new Error('useActiveBoardContext must be used within an ActiveBoardProvider');
  }
  return ctx;
}

export function useActiveBoardId(): string {
  return useActiveBoardContext().boardId;
}

export function useActiveBoardStartDate(): string {
  return useActiveBoardContext().boardStartDate;
}

export function useActiveBoardName(): string {
  return useActiveBoardContext().boardName;
}

export function useActiveBoardColumnsStore(): ColumnsStore {
  return useActiveBoardContext().columnsStore;
}

export function useActiveBoardRequirementsStore(): BoardRequirementsStore {
  return useActiveBoardContext().boardRequirementsStore;
}

export function useActiveBoardTaskViewStore(): BoardTaskViewStore {
  return useActiveBoardContext().boardTaskViewStore;
}
